import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';

import borderRadius from '../../design-system/borders';
import colors from '../../design-system/colors';
import boxShadow from '../../design-system/shadows';
import spacing from '../../design-system/spacings';
import { config as alertConfig } from '../configurations/alertConfig';
import { config as animatedCounterConfig } from '../configurations/animatedCounterConfig';
import { config as articlesConfig } from '../configurations/articlesConfig';
import { config as assetTypeCardConfig } from '../configurations/assetTypeCardConfig';
import { config as assetsCounterConfig } from '../configurations/assetsCounterConfig';
import { config as audioCardListConfig } from '../configurations/audioCardListConfig';
import { config as benefitsV1Config } from '../configurations/benefitsV1Config';
import { config as buttonConfig } from '../configurations/buttonConfig';
import { config as buttonV1Config } from '../configurations/buttonV1Config';
import { config as cardSectionConfig } from '../configurations/cardSectionConfig';
import { config as categoryPillsConfig } from '../configurations/categoryPillsConfig';
import { config as checkboxConfig } from '../configurations/checkboxConfig';
import { config as contentCarouselConfig } from '../configurations/contentCarouselConfig';
import { config as dialogButtonConfig } from '../configurations/dialogButtonConfig';
import { config as dualMediaAndTextConfig } from '../configurations/dualMediaAndTextConfig';
import { config as faqConfig } from '../configurations/faqConfig';
import { config as faqV1Config } from '../configurations/faqV1Config';
import { config as featuredArtistsConfig } from '../configurations/featuredArtistsConfig';
import { config as featuresCardsConfig } from '../configurations/featuresCardsConfig';
import { config as footerHookConfig } from '../configurations/footerHookConfig';
import { config as footerHookLegacyConfig } from '../configurations/footerHookLegacyConfig';
import { config as genresConfig } from '../configurations/genresConfig';
import { config as prominentSearchLegacyConfig } from '../configurations/homepageProminentSearchConfig';
import { configV1 as hubspotV1, configV2 as hubspotV2, configV3 as hubspotV3 } from '../configurations/hubspotConfig';
import { config as hubspotFormSectionConfig } from '../configurations/hubspotFormSectionConfig';
import { config as imageAndTextConfig } from '../configurations/imageAndTextConfig';
import { config as imageCardsConfig } from '../configurations/imageCardsConfig';
import { config as logosConfig } from '../configurations/logosConfig';
import { config as mediaHeroConfig } from '../configurations/mediaHeroConfig';
import { config as mediaTypeChipConfig } from '../configurations/mediaTypeChipConfig';
import { config as moodsConfig } from '../configurations/moodsConfig';
import { config as newTagConfig } from '../configurations/newTagConfig';
import { config as pillsConfig } from '../configurations/pillsConfig';
import { config as planCardConfig } from '../configurations/planCardConfig';
import { config as pricingConfig } from '../configurations/pricingConfig';
import { config as prominentSearchConfig } from '../configurations/prominentSearchConfig';
import { config as richTextConfig } from '../configurations/richTextConfig';
import { config as searchBarConfig } from '../configurations/searchBarConfig';
import { config as searchHeroConfig } from '../configurations/searchHeroConfig';
import { config as seoTextAndImageConfig } from '../configurations/seoTextAndImageConfig';
import { config as seoTextConfig } from '../configurations/seoTextConfig';
import { config as sfxTagConfig } from '../configurations/sfxTagConfig';
import { config as siteMapCategoryConfig } from '../configurations/siteMapCategoryConfig';
import { config as siteMapConfig } from '../configurations/siteMapConfig';
import { config as socialProofConfig } from '../configurations/socialProofConfig';
import { config as stackConfig } from '../configurations/stackConfig';
import { config as staffPickTagConfig } from '../configurations/staffPickTagConfig';
import { config as storyblocksLogoConfig } from '../configurations/storyblocksLogoConfig';
import { config as tabConfig } from '../configurations/tabConfig';
import { config as tabsConfig } from '../configurations/tabsConfig';
import { config as testimonialsConfig } from '../configurations/testimonialsConfig';
import { config as testimonialsV2Config } from '../configurations/testimonialsV2Config';
import { config as textConfig } from '../configurations/textConfig';
import { config as textHeroConfig } from '../configurations/textHeroConfig';
import { config as thankYouConfig } from '../configurations/thankYouConfig';
import { config as tooltipConfig } from '../configurations/tooltipConfig';
import { config as valuePropsConfig } from '../configurations/valuePropsConfig';
import { config as videoCardConfig } from '../configurations/videoCardConfig';
import { config as videoHeroConfig } from '../configurations/videoHeroConfig';
import { config as vimeoConfig } from '../configurations/vimeoConfig';
import { config as wistiaConfig } from '../configurations/wistiaConfig';
import { config as youtubeConfig } from '../configurations/youtubeConfig';

Builder.registerComponent(
    dynamic(() => import('./Alert.builder')),
    alertConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Articles.builder')),
    articlesConfig,
);

Builder.registerComponent(
    dynamic(() => import('./AssetsCounter.builder')),
    assetsCounterConfig,
);

Builder.registerComponent(
    dynamic(() => import('./AnimatedCounter.builder')),
    animatedCounterConfig,
);

Builder.registerComponent(
    dynamic(() => import('./AssetTypeCard.builder')),
    assetTypeCardConfig,
);

Builder.registerComponent(
    dynamic(() => import('./AudioCardList.builder')),
    audioCardListConfig,
);

Builder.registerComponent(
    dynamic(() => import('./BenefitsV1.builder')),
    benefitsV1Config,
);

Builder.registerComponent(
    dynamic(() => import('./Button.builder')),
    buttonConfig,
);

Builder.registerComponent(
    dynamic(() => import('./ButtonV1.builder')),
    buttonV1Config,
);

Builder.registerComponent(
    dynamic(() => import('./CardSection.builder')),
    cardSectionConfig,
);

Builder.registerComponent(
    dynamic(() => import('./CategoryPills.builder')),
    categoryPillsConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Checkbox.builder')),
    checkboxConfig,
);

Builder.registerComponent(
    dynamic(() => import('./ContentCarousel.builder')),
    contentCarouselConfig,
);

Builder.registerComponent(
    dynamic(() => import('./DialogButton.builder')),
    dialogButtonConfig,
);

Builder.registerComponent(
    dynamic(() => import('./DualMediaAndText.builder')),
    dualMediaAndTextConfig,
);

Builder.registerComponent(
    dynamic(() => import('./FAQV1.builder')),
    faqV1Config,
);

Builder.registerComponent(
    dynamic(() => import('./FAQ.builder')),
    faqConfig,
);

Builder.registerComponent(
    dynamic(() => import('./FeaturedArtists.builder')),
    featuredArtistsConfig,
);

Builder.registerComponent(
    dynamic(() => import('./FeaturesCards.builder')),
    featuresCardsConfig,
);

Builder.registerComponent(
    dynamic(() => import('./FooterHookLegacy.builder')),
    footerHookLegacyConfig,
);

Builder.registerComponent(
    dynamic(() => import('./FooterHook.builder')),
    footerHookConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Genres.builder')),
    genresConfig,
);

Builder.registerComponent(
    dynamic(() => import('./HomepageProminentSearch.builder')),
    prominentSearchLegacyConfig,
);

Builder.registerComponent(
    dynamic(() => import('./ProminentSearch.builder')),
    prominentSearchConfig,
);

Builder.registerComponent(
    dynamic(() => import('./RichText.builder')),
    richTextConfig,
);

Builder.registerComponent(
    dynamic(() => import('./SearchHero.builder')),
    searchHeroConfig,
);

Builder.registerComponent(
    dynamic(() => import('./ImageAndText.builder')),
    imageAndTextConfig,
);

Builder.registerComponent(
    dynamic(() => import('./ImageCards.builder')),
    imageCardsConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Logos.builder')),
    logosConfig,
);

Builder.registerComponent(
    dynamic(() => import('./MediaHero.builder')),
    mediaHeroConfig,
);

Builder.registerComponent(
    dynamic(() => import('./MediaTypeChip.builder')),
    mediaTypeChipConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Moods.builder')),
    moodsConfig,
);

Builder.registerComponent(
    dynamic(() => import('./NewTag.builder')),
    newTagConfig,
);

Builder.registerComponent(
    dynamic(() => import('./PlanCardWrapper.builder')),
    planCardConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Pills.builder')),
    pillsConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Pricing.builder')),
    pricingConfig,
);

Builder.registerComponent(
    dynamic(() => import('./SeoText.builder')),
    seoTextConfig,
);

Builder.registerComponent(
    dynamic(() => import('./SeoTextAndImage.builder')),
    seoTextAndImageConfig,
);

Builder.registerComponent(
    dynamic(() => import('./SfxTag.builder')),
    sfxTagConfig,
);

Builder.registerComponent(
    dynamic(() => import('./SiteMap.builder')),
    siteMapConfig,
);

Builder.registerComponent(
    dynamic(() => import('./SocialProof.builder')),
    socialProofConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Stack.builder')),
    stackConfig,
);

Builder.registerComponent(
    dynamic(() => import('./SiteMapCategory.builder')),
    siteMapCategoryConfig,
);

Builder.registerComponent(
    dynamic(() => import('./StaffPickTag.builder')),
    staffPickTagConfig,
);

Builder.registerComponent(
    dynamic(() => import('./StoryblocksLogo.builder')),
    storyblocksLogoConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Tab.builder')),
    tabConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Tabs.builder')),
    tabsConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Testimonials.builder')),
    testimonialsConfig,
);

Builder.registerComponent(
    dynamic(() => import('./TestimonialsV2/TestimonialsV2.builder')),
    testimonialsV2Config,
);

Builder.registerComponent(
    dynamic(() => import('./Text.builder')),
    textConfig,
);

Builder.registerComponent(
    dynamic(() => import('./TextHero.builder')),
    textHeroConfig,
);

Builder.registerComponent(
    dynamic(() => import('./ThankYou.builder')),
    thankYouConfig,
);

Builder.registerComponent(
    dynamic(() => import('./ValueProps.builder')),
    valuePropsConfig,
);

Builder.registerComponent(
    dynamic(() => import('./VideoHero.builder')),
    videoHeroConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Vimeo.builder')),
    vimeoConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Wistia.builder')),
    wistiaConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Youtube.builder')),
    youtubeConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Hubspot.builder')),
    hubspotV1,
);

Builder.registerComponent(
    dynamic(() => import('./Hubspot.builder')),
    hubspotV2,
);

Builder.registerComponent(
    dynamic(() => import('./Hubspot.builder')),
    hubspotV3,
);

Builder.registerComponent(
    dynamic(() => import('./HubspotFormSection/HubspotFormSection.builder')),
    hubspotFormSectionConfig,
);

Builder.registerComponent(
    dynamic(() => import('./SearchBar.builder')),
    searchBarConfig,
);

Builder.registerComponent(
    dynamic(() => import('./VideoCard.builder')),
    videoCardConfig,
);

Builder.registerComponent(
    dynamic(() => import('./Tooltip.builder')),
    tooltipConfig,
);

Builder.register('insertMenu', {
    name: 'Design System - Atoms',
    items: [
        { name: 'Alert' },
        { name: 'Animated Counter' },
        { name: 'Button' },
        { name: 'Button SB' },
        { name: 'Checkbox' },
        { name: 'Logos Component' },
        { name: 'New tag' },
        { name: 'Sfx tag' },
        { name: 'Staff pick tag' },
        { name: 'Storyblocks logo' },
        { name: 'Tab' },
        { name: 'Text SB' },
        { name: 'Tooltip' },
    ],
});

Builder.register('insertMenu', {
    name: 'Design System - Molecules',
    items: [
        { name: 'Asset Type Card' },
        { name: 'Audio Card List' },
        { name: 'Category Pills' },
        { name: 'Dialog Button' },
        { name: 'Faq SB' },
        { name: 'Features Cards' },
        { name: 'Footer Hook SB' },
        { name: 'Hubspot' },
        { name: 'v2:Hubspot' },
        { name: 'v3:Hubspot' },
        { name: 'Media Type Chip' },
        { name: 'Plan Card' },
        { name: 'Prominent Search' },
        { name: 'Social Proof' },
        { name: 'Stack' },
        { name: 'Tabs' },
        { name: 'Testimonials V2' },
        { name: 'Value Props' },
        { name: 'Video Card' },
    ],
});

Builder.register('insertMenu', {
    name: 'Archive - Old/Outdated Components',
    items: [
        { name: 'Articles' },
        { name: 'Assets Counter' },
        { name: 'Benefits (v1)' },
        { name: 'Card Section' },
        { name: 'Content Carousel' },
        { name: 'FAQ' },
        { name: 'Featured Artists' },
        { name: 'Footer Hook' },
        { name: 'Genres' },
        { name: 'Homepage Prominent Search' },
        { name: 'Image And Text' },
        { name: 'Media And Text (Dual)' },
        { name: 'Image Cards' },
        { name: 'Media Hero' },
        { name: 'Moods' },
        { name: 'Pills' },
        { name: 'Pricing' },
        { name: 'Search Bar' },
        { name: 'Search Hero' },
        { name: 'SEO Text And Image' },
        { name: 'SEO Text' },
        { name: 'Site Map Category' },
        { name: 'Site Map' },
        { name: 'Testimonials' },
        { name: 'Text Hero' },
        { name: 'Thank You' },
        { name: 'Video Hero (Legacy)' },
        { name: 'Vimeo' },
        { name: 'Wistia' },
        { name: 'Youtube' },
    ],
});

// Design tokens
Builder.register('editor.settings', {
    allowOverridingTokens: true,
    styleStrictMode: false,
    designTokens: {
        fontFamily: [
            {
                name: 'polysans-neutral',
                value: 'var(--font-polysans-neutral)',
            },
            {
                name: 'polysans-median',
                value: 'var(--font-polysans)',
            },
            {
                name: 'polysans-median-italic',
                value: 'var(--font-polysans-median-italic)',
            },
        ],
        colors,
        borderRadius,
        spacing,
        boxShadow,
    },
});
